import type { AssetFile } from '../../assetDetail.types';
import {
  MAX_ZOOM,
  MIN_ZOOM,
  usePreviewZoom,
  usePreviewZoomReset,
} from '../AssetPreviewOverlay/state/preview-zoom.store';
import { ControlGroup, ControlGroupSeparator } from '../HoverControlsPill';
import { PdfPageNavigator } from '../PdfViewer/PdfPageNavigator';
import { PDF_MAX_ZOOM, PDF_MIN_ZOOM, usePdfViewerStore } from '../PdfViewer/pdfViewer.store';
import { VideoPlayPauseControls } from '../VideoControls';
import { ZoomControls } from '../ZoomControls';

interface Props {
  file: AssetFile;
}

/**
 * Asset-type-specific control cluster rendered in the fullscreen top bar.
 * - image: zoom in / % / zoom out
 * - video: play/pause + timecode (scrubber stays in the bottom of VideoOverlay)
 * - document (pdf): page navigator + zoom controls
 * - other types: nothing
 */
export function FullscreenAssetControls({ file }: Props) {
  if (file.assetType === 'image') return <ImageControls />;
  if (file.assetType === 'video')
    return (
      <ControlGroup>
        <VideoPlayPauseControls />
      </ControlGroup>
    );
  if (file.assetType === 'document') return <PdfControls />;
  return null;
}

function ImageControls() {
  const { zoom, zoomIn, zoomOut } = usePreviewZoom();
  const reset = usePreviewZoomReset();
  const canZoomOut = zoom > MIN_ZOOM + 1e-6;
  const canZoomIn = zoom < MAX_ZOOM - 1e-6;
  const canReset = Math.abs(zoom - 1) > 1e-6;
  return (
    <ControlGroup>
      <ZoomControls
        zoom={zoom}
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
        canReset={canReset}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onReset={reset}
      />
    </ControlGroup>
  );
}

function PdfControls() {
  const zoomLevel = usePdfViewerStore((s) => s.zoomLevel);
  const zoomIn = usePdfViewerStore((s) => s.zoomIn);
  const zoomOut = usePdfViewerStore((s) => s.zoomOut);
  const resetZoom = usePdfViewerStore((s) => s.resetZoom);
  const canZoomIn = zoomLevel < PDF_MAX_ZOOM - 1e-6;
  const canZoomOut = zoomLevel > PDF_MIN_ZOOM + 1e-6;
  const canResetZoom = Math.abs(zoomLevel - 1) > 1e-6;
  return (
    <ControlGroup>
      <PdfPageNavigator />
      <ControlGroupSeparator />
      <ZoomControls
        zoom={zoomLevel}
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
        canReset={canResetZoom}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onReset={resetZoom}
      />
    </ControlGroup>
  );
}
